import * as _ from 'lodash';
import { Nullable } from 'global';
import { removeGraphQLMEtaFields } from 'utils/removeGraphQLMEtaFields';
import {
  Markup,
  ProposalSaleType,
  LenderMarketRateInput,
  SubmitProposalOptionActionType,
  CreditSubmissionDecisionFeeType,
} from '@trnsact/trnsact-shared-types';
import { prepareMenuOptionsForAPI } from './prepareMenuOptionsForAPI';
import { CreateProposalVariables } from '../api/types';
import {
  AftermarketMenu,
  FinanceProgramFormValues,
  MarkupMethods,
  OptionName,
  Pricing,
  ProductConfig,
  ProductId,
  SaleType,
  SendVia,
  Structures,
  Term,
} from '../types';
import { convertFeeFormValueToFee } from './convertFeeFormValueToFee';
/**
 * Safeguard to clean up empty HTML content
 * @param value - string | undefined
 * @returns Nullable<string>
 */
function cleanEmptyHtml(value: string | undefined): Nullable<string> {
  if (!value) return null;

  // Remove HTML tags and check if there's any actual content
  const textContent = value.replace(/<[^>]*>/g, '').trim();

  // If no text content remains, return null
  if (!textContent) return null;

  return value;
}

interface Options {
  voId: string;
  isDraft: boolean;
  pricing: Record<Term, Pricing>;
  parentProposalId?: Nullable<string>;
  formData: FinanceProgramFormValues;
  vendorContactId?: Nullable<string>;
  submitType?: SubmitProposalOptionActionType;
  menuByTerms: Record<Term, AftermarketMenu>;
  commonProductsConfiguration: Partial<ProductConfig>;
  productsConfigInMenu: Record<OptionName, Record<ProductId, ProductConfig>>;
}

export function collectVariablesForCreateProposal({
  voId,
  isDraft,
  formData,
  pricing,
  menuByTerms,
  parentProposalId,
  productsConfigInMenu,
  submitType = SubmitProposalOptionActionType.AcceptOption,
  commonProductsConfiguration,
  vendorContactId,
}: Options): CreateProposalVariables {
  const {
    financeQuote: {
      fee,
      terms,
      rateType,
      commonRate,
      programName,
      daysToFirstPayment,
      specifyRate,
      specifyRates,
      advancePayment,
      markupRate,
      residual,
      paymentCalculationMethod,
      programType,
      structure,
      markupMethod,
    },
    saleDetails: { financeAmount, saleType, downPayment },
    financeProgramDescription,
    submission: { maxApprovalAmount },
    displaySettings: { excludeEquipmentSales },
    equipment,
    notesToBuyer,
  } = formData;

  let dealerMarkup: Nullable<Markup> = null;

  if (formData.financeQuote.structure !== Structures.None && formData.financeQuote.structure !== Structures.Subsidy) {
    if (formData.financeQuote.markupMethod === MarkupMethods.MarkupPercentage) {
      dealerMarkup = { identifier: 'Dealer Markup', markupPercentage: Number(formData.financeQuote.markupRate) };
    } else if (formData.financeQuote.markupMethod === MarkupMethods.MarkupDollar) {
      dealerMarkup = { identifier: 'Dealer Markup', markupFlat: Number(formData.financeQuote.markupRate) };
    }
  }

  let lenderMarketRatesToSend: LenderMarketRateInput[] = [];

  if (
    formData.financeQuote.subsidyCapConfiguration?.lenderMarketRates &&
    formData.financeQuote.subsidyCapConfiguration?.lenderMarketRates?.length > 0
  ) {
    try {
      const termsToUse = _.orderBy(terms.map(t => Number(t)));

      if (formData.financeQuote.specifyRate && formData.financeQuote.specifyLenderMarketRates?.length > 0) {
        lenderMarketRatesToSend = formData.financeQuote.specifyLenderMarketRates?.map((rate, index) => ({
          term: termsToUse[index] ?? 0,
          rate: Number(
            formData.financeQuote.specifyRate
              ? (formData.financeQuote.specifyLenderMarketRates[index]?.rate ?? 0)
              : formData.financeQuote.commonLenderMarketRate
          ),
        }));
      } else {
        lenderMarketRatesToSend = formData.financeQuote.subsidyCapConfiguration?.lenderMarketRates?.map(
          (rate, index) => ({
            term: _.orderBy(termsToUse, 'term', 'asc')[index] ?? 0,
            rate: Number(formData.financeQuote.commonLenderMarketRate),
          })
        );
      }
    } catch (e: any) {
      console.error('collectFinanceVariablesForCalc: Error converting lender market rates', e.message);
      lenderMarketRatesToSend = [];
    }
  }

  const collectedData = {
    input: {
      sendProposalData: !isDraft
        ? {
            sendViaEmail: [SendVia.Email, SendVia.TextAndEmail].includes(formData.sendVia.type),
            sendViaText: [SendVia.Text, SendVia.TextAndEmail].includes(formData.sendVia.type),
            phoneNumber: formData.contact.phone,
            ccEmail: formData.ccEmail.map(o => o.email),
          }
        : null,
      vendorOpportunityId: voId,
      parentProposalId: parentProposalId || null,
      proposalMenuId: formData.financeQuote?.menu || null,
      financeProgramId: formData.financeQuote?.program || null,
      financeProgramDescription: financeProgramDescription.apiValue ?? null,
      notesToBuyer: cleanEmptyHtml(notesToBuyer),
      saleType: determineCreateType({ saleType, isMenuExist: !!Object.keys(menuByTerms).length }),
      settings: {
        configuration: {
          formData,
          pricing,
          builder: menuByTerms ? JSON.stringify(removeGraphQLMEtaFields(menuByTerms)) : null,
          productsConfig: productsConfigInMenu ? JSON.stringify(removeGraphQLMEtaFields(productsConfigInMenu)) : null,
          commonProductsConfiguration: commonProductsConfiguration ? JSON.stringify(commonProductsConfiguration) : null,
        },
        view: {
          isShowProductDetails: formData.displaySettings.productDetails,
          isShowProductPrice: formData.displaySettings.productPrice,
          isShowLenderLogo: formData.displaySettings.showLenderLogo,
          isShowLenderName: formData.displaySettings.showLenderName,
          isShowInterestRate: formData.displaySettings.showInterestRate,
          isShowBestOptions: formData.displaySettings.showBestOptions,
          excludeEquipmentSales: formData.displaySettings.excludeEquipmentSales,
          isShowFinanceProgramDescription: formData.displaySettings.showFinanceProgramDescription,
        },
        behavior: {
          submitOptionActionType: submitType,
        },
        meta: {
          equipmentId: equipment.selectedEquipment ?? null,
        },
        oca: {
          lenderProfileId: formData.displaySettings.lenderProfileId ?? null,
          vendorContactId,
        },
      },
      paymentOptions:
        saleType === SaleType.Financing
          ? {
              finance: {
                saleType: determineCreateType({ saleType, isMenuExist: !!Object.keys(menuByTerms).length }),
                salesTaxPercentage: 0,
                numberOfAdvancePayments: Number(advancePayment) ?? 0,
                financeProgramType: programType.toUpperCase(),
                rateType,
                financeProgramName: programName,
                amountFinanced: excludeEquipmentSales ? 0 : financeAmount,
                downPaymentAmount: Number(downPayment.flat) ?? 0,
                residualPercentage: Number(residual) ?? 0,
                daysToFirstPayment: Number(daysToFirstPayment),
                startDate: new Date(formData.financeQuote.startDate).toISOString().split('T')[0],
                menu: prepareMenu(
                  menuByTerms,
                  financeProgramDescription.apiValue,
                  formData.financeQuote.menu,
                  productsConfigInMenu
                ),
                customerRate:
                  (structure !== Structures.None && markupMethod === MarkupMethods.CustomerRate) ||
                  structure === Structures.Subsidy
                    ? Number(markupRate)
                    : null,
                dealerMarkup,
                paymentPeriodTiming: paymentCalculationMethod,
                markups: [],
                terms: prepareRates({ specifyRate, commonRate, specifyRates, terms }),
                flatFees: fee
                  .filter(fee => fee.type === CreditSubmissionDecisionFeeType.Flat)
                  .map(convertFeeFormValueToFee),
                percentageFees: fee
                  .filter(fee => fee.type === CreditSubmissionDecisionFeeType.Percentage)
                  .map(convertFeeFormValueToFee),
                maxApprovalAmount,
                isSubsidized: formData.financeQuote.structure === Structures.Subsidy,
                subsidyCapConfiguration:
                  formData.financeQuote.structure === Structures.Subsidy && formData.financeQuote.configureSubsidyCap
                    ? { ...formData.financeQuote.subsidyCapConfiguration, lenderMarketRates: lenderMarketRatesToSend }
                    : null,
                minimumDownPaymentPercentage: formData.financeQuote.minimumRequiredDownPayment
                  ? Number(formData.financeQuote.minimumRequiredDownPayment)
                  : null,
              },
            }
          : {
              cash: {
                amountFinanced: excludeEquipmentSales ? 0 : financeAmount,
                flatFees: fee
                  .filter(fee => fee.type === CreditSubmissionDecisionFeeType.Flat)
                  .map(convertFeeFormValueToFee),
                percentageFees: fee
                  .filter(fee => fee.type === CreditSubmissionDecisionFeeType.Percentage)
                  .map(convertFeeFormValueToFee),
                menu: prepareMenu(
                  menuByTerms,
                  financeProgramDescription.apiValue,
                  formData.financeQuote.menu,
                  productsConfigInMenu
                ),
                saleType: ProposalSaleType.CashWithAftermarketMenu,
              },
            },
    },
  };

  return collectedData;
}

function determineCreateType(options: { saleType: SaleType; isMenuExist: boolean }): ProposalSaleType {
  if (!options.isMenuExist) return ProposalSaleType.FinanceOnly;

  return options.saleType === SaleType.Cash
    ? ProposalSaleType.CashWithAftermarketMenu
    : ProposalSaleType.FinanceWithAftermarketMenu;
}

function prepareMenu(
  menuByTerms: Record<Term, AftermarketMenu> = {},
  financeProgramDescription: string,
  menuId: string | null,
  productsConfigInMenu: Record<OptionName, Record<ProductId, ProductConfig>>
) {
  const menuArr = Object.values(menuByTerms);

  if (!menuArr.length) return null;

  return {
    name: menuArr[0].name,
    proposalMenuId: menuId ?? '',
    menuOptions: prepareMenuOptionsForAPI(menuArr[0]?.menuOptions, financeProgramDescription, productsConfigInMenu),
  };
}

export function prepareRates({
  terms,
  specifyRate,
  commonRate,
  specifyRates,
}: Pick<FinanceProgramFormValues['financeQuote'], 'specifyRates' | 'commonRate' | 'specifyRate' | 'terms'>) {
  if (!specifyRate) return terms.map(term => ({ term: Number(term), rate: commonRate }));

  return specifyRates.map(({ rate, term }) => ({ term: Number(term), rate }));
}
